// Module Registry — single source of truth for all toggleable modules.
// Used by AuthContext (enablement check), PageLayout (nav filtering),
// ProtectedRoute (route guard), and SuperAdmin (manage modules UI).

export const ALWAYS_ENABLED = [
  { id: 'dashboard', name: 'Dashboard', route: '/dashboard' },
  { id: 'settings_company', name: 'My Company', route: '/settings/company' },
  { id: 'settings_users', name: 'Users & Roles', route: '/settings/users' },
  { id: 'settings_backup', name: 'Backup', route: '/settings/backup' },
];

export const ALWAYS_ENABLED_IDS = new Set(ALWAYS_ENABLED.map(m => m.id));

// Branch module ID — used by AuthContext and other files to check if multi-branch is enabled
export const BRANCH_MODULE_ID = 'settings_branches';

export const POS_MODULE_IDS = ['settings_pos_counters', 'pos_counter_sale'];

// Modules that are disabled by default — must be explicitly enabled by super admin
// Non-tax invoice module IDs — disabled by default, enabled via super admin.
// Gives the company an add-on path for non-tax (cash/unregistered) documents
// that skips GST postings but still affects A/R, A/P, inventory, and WHT.
export const NON_TAX_MODULE_IDS = [
  'sales_non_tax_invoice',
  'sales_non_tax_return',
  'purchases_non_tax_invoice',
  'purchases_non_tax_return',
];

// Attendance machine add-on (biometric device → payroll). Disabled by default,
// enabled via super admin per company. Independent of SALARY_MODULE_IDS so a
// company can have Salary without Attendance.
export const ATTENDANCE_MODULE_IDS = [
  'salary_attendance',
  'salary_attendance_settings',
];

// Fixed Assets module IDs — disabled by default, enabled via super admin.
// Asset register + depreciation + disposal, all riding the unified transaction
// architecture. Phase 1 ships register + categories; depreciation/disposal pages
// and asset reports are added in later phases.
export const FIXED_ASSETS_MODULE_IDS = [
  'fixed_assets_register',
  'fixed_assets_categories',
  'fixed_assets_depreciation',
  'fixed_assets_disposal',
  'fixed_assets_appreciation',
  'reports_asset_register',
  'reports_depreciation_schedule',
  'reports_fbr_depreciation',
  'reports_asset_disposals',
  'reports_asset_movement',
];

// Job report module IDs — disabled by default, enabled per company by super admin.
// Account-type (COA) driven job costing built on the unified transaction architecture.
export const JOB_REPORT_MODULE_IDS = [
  'reports_job_cost_sheet',
  'reports_job_profit_loss',
];

// Production report module IDs — disabled by default, enabled per company by super admin.
// (Populated as each production report ships.)
export const PRODUCTION_REPORT_MODULE_IDS = [
  'reports_production_summary',
  'reports_production_variance',
];

// Customer-wise items + custom pricing module. When ON, customers gain a
// "Custom Items & Pricing" tab; sales forms (SI/NSI/QT/SO/DC/SR/NSR/POS)
// auto-fill unitPrice from the customer's price list and (when the customer
// is flagged exclusive) restrict the item picker to that list. Default off.
export const CUSTOMER_PRICING_MODULE_ID = 'sales_customer_pricing';

// Modules ON by default — Store, Salary, Import Purchase, and AI Studio are now
// enabled for every new company (user-attraction decision, 2026-05-22). They are
// no longer gated behind super admin. Items deliberately kept disabled below are
// the genuinely sensitive / niche add-ons (multi-branch, WhatsApp, shadow books,
// MRP, customer pricing, etc.).
export const DEFAULT_DISABLED_IDS = new Set([BRANCH_MODULE_ID, ...POS_MODULE_IDS, ...NON_TAX_MODULE_IDS, 'reports_salesman_incentive', 'store_branch_transfer', 'unified_party_picker', 'settings_inventory_movement', 'master_units', CUSTOMER_PRICING_MODULE_ID, 'customer_subcategory_discount', 'purchases_vendor_pricing', 'reports_purchase_cycle', 'reports_sale_cycle', 'reports_grn_tracking', 'reports_dc_tracking', 'mrp_pricing', 'transaction_attachments', 'master_barcode_labels', 'purchases_enforce_grn_before_pi', 'purchases_line_expense_accounts', 'whatsapp_connect', 'whatsapp_templates', 'whatsapp_logs', 'whatsapp_settings', 'inventory_block_negative', 'item_has_size', 'item_two_units', 'pos_weight_scale', 'pos_price_type', 'store_material_issue', 'store_production_receipt', 'reports_material_issue', 'reports_production_receipt', 'cash_book', 'store_stock_audit', 'reports_stock_audit_list', 'reports_count_difference', 'reports_stock_loss', 'reports_missing_items', 'reports_user_activity', 'reports_stock_transfer', 'reports_branch_transfer', 'store_send_inventory', 'store_receive_inventory', 'reports_transfer_report', 'fbr_pos', 'fbr_digital_invoicing', 'customer_class', 'sales_third_schedule_tax', 'pos_bank_discounts', 'print_invoice_letterheads', ...FIXED_ASSETS_MODULE_IDS, ...JOB_REPORT_MODULE_IDS, ...PRODUCTION_REPORT_MODULE_IDS, ...ATTENDANCE_MODULE_IDS]);

// Each module can optionally declare a `permission: { category, key }` pointing
// at the role permissions matrix in `UsersRoles.js > permissionCategories`.
// Routes without `permission` are accessible to any authenticated company member
// (dashboard, always-on settings). Routes with `permission` are gated by
// `hasPermission(category, key, 'view')` in route guard + sidebar filter.
export const MODULE_GROUPS = [
  {
    name: 'Settings',
    modules: [
      { id: 'settings_financial_year', name: 'Financial Year', route: '/settings/financial-year', permission: { category: 'settings', key: 'financialYear' } },
      { id: 'settings_documents', name: 'Document Settings', route: '/settings/documents', permission: { category: 'settings', key: 'documentSettings' } },
      { id: 'settings_print_formats', name: 'Print Formats', route: '/settings/print-formats', permission: { category: 'settings', key: 'printFormats' } },
      { id: 'settings_branches', name: 'Branch Setup', route: '/settings/branches', permission: { category: 'settings', key: 'branchSetup' } },
      { id: 'settings_pos_counters', name: 'POS Counters', route: '/settings/pos-counters', permission: { category: 'settings', key: 'posCounterSetup' } },
      { id: 'settings_inventory_movement', name: 'Inventory Movement', route: '/settings/inventory-movement', permission: { category: 'settings', key: 'inventoryMovement' } },
    ]
  },
  {
    name: 'Master Data',
    modules: [
      { id: 'master_chart_of_accounts', name: 'Chart of Accounts', route: '/master/chart-of-accounts', permission: { category: 'masterData', key: 'chartOfAccounts' } },
      { id: 'master_customers', name: 'Customers', route: '/master/customers', permission: { category: 'masterData', key: 'customers' } },
      { id: 'master_vendors', name: 'Vendors', route: '/master/vendors', permission: { category: 'masterData', key: 'vendors' } },
      { id: 'master_banks', name: 'Banks', route: '/master/banks', permission: { category: 'masterData', key: 'banks' } },
      { id: 'master_chart_of_inventory', name: 'Items/Products', route: '/master/chart-of-inventory', permission: { category: 'masterData', key: 'items' } },
      { id: 'maintain_bulk_item_update', name: 'Bulk Item Update', route: '/master/bulk-item-update', permission: { category: 'masterData', key: 'bulkItemUpdate' } },
      { id: 'master_inventory_locations', name: 'Inventory Locations', route: '/master/inventory-locations', permission: { category: 'masterData', key: 'locations' } },
      { id: 'master_employees', name: 'Employees', route: '/master/employees', permission: { category: 'masterData', key: 'employees' } },
      { id: 'master_regions', name: 'Regions', route: '/master/regions', permission: { category: 'masterData', key: 'regions' } },
      { id: 'master_jobs', name: 'Jobs / Projects', route: '/master/jobs', permission: { category: 'masterData', key: 'jobs' } },
      { id: 'master_opening_balances', name: 'Opening Balances', route: '/master/opening-balances', permission: { category: 'masterData', key: 'openingBalances' } },
      { id: 'master_import_charge_templates', name: 'Import Charge Templates', route: '/master/import-charge-templates', permission: { category: 'masterData', key: 'importChargeTemplates' } },
      { id: 'master_units', name: 'Units of Measure', route: '/master/units', permission: { category: 'masterData', key: 'units' } },
      // Bulk barcode label printing for Zebra / TSC thermal printers. Disabled by
      // default — SuperAdmin enables per company. Reuses the `items` permission.
      { id: 'master_barcode_labels', name: 'Barcode Labels', route: '/inventory/labels', permission: { category: 'masterData', key: 'items' } },
    ]
  },
  {
    name: 'Purchases',
    modules: [
      { id: 'purchases_purchase_order', name: 'Purchase Order', route: '/purchases/purchase-order', permission: { category: 'transactions', key: 'purchaseOrder' } },
      { id: 'purchases_grn', name: 'Goods Receipt (GRN)', route: '/purchases/grn', permission: { category: 'transactions', key: 'grn' } },
      { id: 'purchases_purchase_invoice', name: 'Purchase Invoice', route: '/purchases/purchase-invoice', permission: { category: 'transactions', key: 'purchaseInvoice' } },
      { id: 'purchases_purchase_return', name: 'Purchase Return', route: '/purchases/purchase-return', permission: { category: 'transactions', key: 'purchaseReturn' } },
      { id: 'purchases_non_tax_invoice', name: 'Non-Tax Purchase Invoice', route: '/purchases/non-tax-invoice', permission: { category: 'transactions', key: 'nonTaxPurchaseInvoice' } },
      { id: 'purchases_non_tax_return', name: 'Non-Tax Purchase Return', route: '/purchases/non-tax-purchase-return', permission: { category: 'transactions', key: 'nonTaxPurchaseReturn' } },
      { id: 'purchases_import_invoice', name: 'Import Purchase Invoice', route: '/purchases/import-invoice', permission: { category: 'transactions', key: 'importPurchaseInvoice' } },
    ]
  },
  {
    name: 'Sales',
    modules: [
      { id: 'sales_quotation', name: 'Quotation', route: '/sales/quotation', permission: { category: 'transactions', key: 'quotation' } },
      { id: 'sales_sale_order', name: 'Sale Order', route: '/sales/sale-order', permission: { category: 'transactions', key: 'saleOrder' } },
      { id: 'sales_delivery_challan', name: 'Delivery Challan', route: '/sales/delivery-challan', permission: { category: 'transactions', key: 'deliveryChallan' } },
      { id: 'sales_sale_invoice', name: 'Sale Invoice', route: '/sales/sale-invoice', permission: { category: 'transactions', key: 'saleInvoice' } },
      { id: 'sales_sale_return', name: 'Sale Return', route: '/sales/sale-return', permission: { category: 'transactions', key: 'saleReturn' } },
      { id: 'sales_non_tax_invoice', name: 'Non-Tax Sale Invoice', route: '/sales/non-tax-invoice', permission: { category: 'transactions', key: 'nonTaxSaleInvoice' } },
      { id: 'sales_non_tax_return', name: 'Non-Tax Sale Return', route: '/sales/non-tax-sale-return', permission: { category: 'transactions', key: 'nonTaxSaleReturn' } },
      // FBR POS (Tier-1) — real-time posting of Sale Invoices to FBR (fiscal no. + QR).
      // Feature toggle only (no menu route); the "Send to FBR" button appears on the
      // Sale Invoice when enabled. Config (POS ID / token) is set in Super Admin.
      { id: 'fbr_pos', name: 'FBR POS', route: null },
      // FBR Digital Invoicing (e-Invoicing) — STANDALONE, separate from FBR POS.
      // Real-time posting of Sale Invoices to FBR PRAL (production), returns the
      // IRN + QR to print. Feature toggle only (no menu route); the "Send to FBR
      // (Digital Invoice)" button appears on the Sale Invoice when enabled.
      // Default OFF — zero effect on companies without it.
      { id: 'fbr_digital_invoicing', name: 'FBR Digital Invoicing', route: null },
    ]
  },
  {
    name: 'POS',
    modules: [
      { id: 'pos_counter_sale', name: 'POS Counter Sale', route: '/pos', permission: { category: 'transactions', key: 'posCounterSale' } },
    ]
  },
  {
    name: 'Store',
    modules: [
      { id: 'store_purchase_requisition', name: 'Purchase Requisition', route: '/store/purchase-requisition', permission: { category: 'store', key: 'purchaseRequisition' } },
      { id: 'store_inward_gate_pass', name: 'Inward Gate Pass', route: '/store/inward-gate-pass', permission: { category: 'store', key: 'inwardGatePass' } },
      { id: 'store_outward_gate_pass', name: 'Outward Gate Pass', route: '/store/outward-gate-pass', permission: { category: 'store', key: 'outwardGatePass' } },
      { id: 'store_bill_of_materials', name: 'Bill of Materials', route: '/store/bill-of-materials', permission: { category: 'store', key: 'billOfMaterials' } },
      { id: 'store_production_order', name: 'Production Order', route: '/store/production-order', permission: { category: 'store', key: 'productionOrder' } },
      { id: 'store_material_issue', name: 'Material Issue to Production', route: '/store/material-issue', permission: { category: 'store', key: 'materialIssue' } },
      { id: 'store_production_receipt', name: 'Production Receipt', route: '/store/production-receipt', permission: { category: 'store', key: 'productionReceipt' } },
      { id: 'store_add_stock', name: 'Add Stock', route: '/store/add-stock', permission: { category: 'transactions', key: 'addStock' } },
      { id: 'store_reduce_stock', name: 'Reduce Stock', route: '/store/reduce-stock', permission: { category: 'transactions', key: 'reduceStock' } },
      // Physical stock count. No `permission` key on purpose: adding a new key to an
      // existing permission category would NOT back-fill onto existing roles (they'd
      // silently lose access), so access is module-gated and the risky actions
      // (start / approve / cancel an audit) are restricted to company admins in-page.
      { id: 'store_stock_audit', name: 'Stock Audit', route: '/store/stock-audit' },
      { id: 'store_stock_transfer', name: 'Stock Transfer', route: '/store/stock-transfer', permission: { category: 'transactions', key: 'stockTransfer' } },
      { id: 'store_branch_transfer', name: 'Branch Transfer', route: '/store/branch-transfer', permission: { category: 'transactions', key: 'stockTransfer' } },
      { id: 'store_send_inventory', name: 'Send Inventory', route: '/store/send-inventory', permission: { category: 'transactions', key: 'stockTransfer' } },
      { id: 'store_receive_inventory', name: 'Receive Inventory', route: '/store/receive-inventory', permission: { category: 'transactions', key: 'stockTransfer' } },
    ]
  },
  {
    name: 'Receipts',
    modules: [
      { id: 'receipts_cash_receipt', name: 'Cash Receipt', route: '/receipts/cash-receipt', permission: { category: 'transactions', key: 'cashReceipt' } },
      { id: 'receipts_bank_receipt', name: 'Bank Receipt', route: '/receipts/bank-receipt', permission: { category: 'transactions', key: 'bankReceipt' } },
    ]
  },
  {
    name: 'Payments',
    modules: [
      { id: 'payments_cash_payment', name: 'Cash Payment', route: '/payments/cash-payment', permission: { category: 'transactions', key: 'cashPayment' } },
      { id: 'payments_bank_payment', name: 'Bank Payment', route: '/payments/bank-payment', permission: { category: 'transactions', key: 'bankPayment' } },
    ]
  },
  {
    name: 'Journal',
    modules: [
      { id: 'journal_general', name: 'General Journal', route: '/journal/general', permission: { category: 'transactions', key: 'journalVoucher' } },
      { id: 'cash_book', name: 'Cash Book', route: '/journal/cash-book', permission: { category: 'transactions', key: 'cashBook' } },
    ]
  },
  {
    name: 'Salary',
    modules: [
      { id: 'salary_staff', name: 'Salary Staff', route: '/salary/staff', permission: { category: 'salary', key: 'salaryStaff' } },
      { id: 'salary_advance', name: 'Advance', route: '/salary/advance', permission: { category: 'salary', key: 'advance' } },
      { id: 'salary_loan', name: 'Loan', route: '/salary/loan', permission: { category: 'salary', key: 'loan' } },
      { id: 'salary_loan_recovery', name: 'Loan Recovery', route: '/salary/loan-recovery', permission: { category: 'salary', key: 'loanRecovery' } },
      { id: 'salary_payroll_run', name: 'Payroll Run', route: '/salary/payroll-run', permission: { category: 'salary', key: 'payrollRun' } },
      { id: 'salary_final_settlement', name: 'Final Settlement', route: '/salary/final-settlement', permission: { category: 'salary', key: 'finalSettlement' } },
      { id: 'salary_attendance', name: 'Attendance', route: '/salary/attendance', permission: { category: 'salary', key: 'attendance' } },
      { id: 'salary_attendance_settings', name: 'Attendance Settings', route: '/salary/attendance-settings', permission: { category: 'salary', key: 'attendanceSettings' } },
    ]
  },
  {
    name: 'Fixed Assets',
    modules: [
      { id: 'fixed_assets_register', name: 'Asset Register', route: '/assets/register', permission: { category: 'fixedAssets', key: 'assets' } },
      { id: 'fixed_assets_categories', name: 'Asset Categories', route: '/assets/categories', permission: { category: 'fixedAssets', key: 'assets' } },
      { id: 'fixed_assets_depreciation', name: 'Depreciation Run', route: '/assets/depreciation', permission: { category: 'fixedAssets', key: 'depreciation' } },
      { id: 'fixed_assets_disposal', name: 'Asset Disposal', route: '/assets/disposal', permission: { category: 'fixedAssets', key: 'disposal' } },
      { id: 'fixed_assets_appreciation', name: 'Asset Appreciation', route: '/assets/appreciation', permission: { category: 'fixedAssets', key: 'appreciation' } },
    ]
  },
  {
    name: 'Reports - Fixed Assets',
    modules: [
      { id: 'reports_asset_register', name: 'Fixed Asset Register', route: '/reports/asset-register', permission: { category: 'fixedAssets', key: 'reports' } },
      { id: 'reports_depreciation_schedule', name: 'Depreciation Schedule', route: '/reports/depreciation-schedule', permission: { category: 'fixedAssets', key: 'reports' } },
      { id: 'reports_fbr_depreciation', name: 'FBR Depreciation Statement', route: '/reports/fbr-depreciation', permission: { category: 'fixedAssets', key: 'reports' } },
      { id: 'reports_asset_disposals', name: 'Asset Disposals', route: '/reports/asset-disposals', permission: { category: 'fixedAssets', key: 'reports' } },
      { id: 'reports_asset_movement', name: 'Asset Movement', route: '/reports/asset-movement', permission: { category: 'fixedAssets', key: 'reports' } },
    ]
  },
  {
    name: 'Reports - Main',
    modules: [
      { id: 'reports_journal', name: 'Journal Report', route: '/reports/journal', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_vouchers', name: 'Print Vouchers', route: '/reports/vouchers', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_serial_tracking', name: 'Serial Tracking', route: '/reports/serial-tracking', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_po_tracking', name: 'PO Tracking', route: '/reports/po-tracking', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_so_tracking', name: 'SO Tracking', route: '/reports/so-tracking', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_grn_tracking', name: 'GRN Tracking', route: '/reports/grn-tracking', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_dc_tracking', name: 'DC Tracking', route: '/reports/dc-tracking', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_drafts', name: 'Draft Report', route: '/reports/drafts', permission: { category: 'reports', key: 'financialReports' } },
      // Control / audit-trail report — who did what. Sold as an add-on, so default OFF.
      { id: 'reports_user_activity', name: 'User Activity Log', route: '/reports/user-activity', permission: { category: 'reports', key: 'financialReports' } },
    ]
  },
  {
    name: 'Reports - Purchase',
    modules: [
      { id: 'reports_purchase_summary', name: 'Purchase Summary', route: '/reports/purchase-summary', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_purchase_register', name: 'Purchase Register', route: '/reports/purchase-register', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_party_purchase_summary', name: 'Party Purchase Summary', route: '/reports/party-purchase-summary', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_vendor_payments', name: 'Vendor Payments', route: '/reports/vendor-payments', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_purchase_activity', name: 'Purchase Activity', route: '/reports/purchase-activity', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_item_purchase_analysis', name: 'Item Purchase Analysis', route: '/reports/item-purchase-analysis', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_ap_aging', name: 'Accounts Payable Aging', route: '/reports/ap-aging', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_vendor_balances', name: 'Vendor Balances', route: '/reports/vendor-balances', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_landed_cost_analysis', name: 'Landed Cost Analysis', route: '/reports/landed-cost-analysis', permission: { category: 'reports', key: 'purchaseReports' } },
      { id: 'reports_purchase_cycle', name: 'Purchase Cycle (Document Flow)', route: '/reports/purchase-cycle', permission: { category: 'reports', key: 'purchaseReports' } },
    ]
  },
  {
    name: 'Reports - Sale',
    modules: [
      { id: 'reports_sale_summary', name: 'Sale Summary', route: '/reports/sale-summary', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_sale_register', name: 'Sale Register', route: '/reports/sale-register', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_pos_sales', name: 'POS Sales', route: '/reports/pos-sales', permission: { category: 'reports', key: 'posReports' } },
      { id: 'reports_pos_shifts', name: 'POS Shift / Z Report', route: '/reports/pos-shifts', permission: { category: 'reports', key: 'posReports' } },
      { id: 'reports_pos_branch_cash', name: 'Branch Cash Position', route: '/reports/pos-branch-cash', permission: { category: 'reports', key: 'posReports' } },
      { id: 'reports_party_sale_summary', name: 'Party Sale Summary', route: '/reports/party-sale-summary', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_collection_report', name: 'Collection Report', route: '/reports/collection-report', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_sale_activity', name: 'Sale Activity', route: '/reports/sale-activity', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_item_sale_analysis', name: 'Item Sale Analysis', route: '/reports/item-sale-analysis', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_ar_aging', name: 'Accounts Receivable Aging', route: '/reports/ar-aging', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_salesperson_performance', name: 'Salesperson Performance', route: '/reports/salesperson-performance', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_salesman_incentive', name: 'Salesman Incentive', route: '/reports/salesman-incentive', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_item_profit_loss', name: 'Item-wise Profit & Loss', route: '/reports/item-profit-loss', permission: { category: 'reports', key: 'profitReports' } },
      { id: 'reports_invoice_profit_loss', name: 'Invoice-wise Profit & Loss', route: '/reports/invoice-profit-loss', permission: { category: 'reports', key: 'profitReports' } },
      { id: 'reports_customer_balances', name: 'Customer Balances', route: '/reports/customer-balances', permission: { category: 'reports', key: 'saleReports' } },
      { id: 'reports_sales_intelligence', name: 'Sales Intelligence', route: '/reports/sales-intelligence', permission: { category: 'reports', key: 'intelligenceReports' } },
      { id: 'reports_operational_metrics', name: 'Operational Metrics', route: '/reports/operational-metrics', permission: { category: 'reports', key: 'intelligenceReports' } },
      { id: 'reports_sale_cycle', name: 'Sale Cycle (Document Flow)', route: '/reports/sale-cycle', permission: { category: 'reports', key: 'saleReports' } },
    ]
  },
  {
    name: 'Reports - Inventory',
    modules: [
      { id: 'reports_inventory_ledger', name: 'Inventory Ledger', route: '/reports/inventory-ledger', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_inventory_balances', name: 'Inventory Balances', route: '/reports/inventory-balances', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_low_stock_alert', name: 'Low Stock Alert', route: '/reports/low-stock-alert', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_inventory_intelligence', name: 'Inventory Intelligence', route: '/reports/inventory-intelligence', permission: { category: 'reports', key: 'intelligenceReports' } },
      { id: 'reports_stock_transfer', name: 'Stock Transfer Report', route: '/reports/stock-transfer', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_branch_transfer', name: 'Branch Transfer Report', route: '/reports/branch-transfer', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_transfer_report', name: 'Transfer Report', route: '/reports/transfer-report', permission: { category: 'reports', key: 'inventoryReports' } },
    ]
  },
  {
    // Stock Audit reports live in their OWN group (not mixed into Inventory) so
    // they can be sold/enabled as one bundle with the Stock Audit feature.
    name: 'Reports - Audit',
    modules: [
      { id: 'reports_stock_audit_list', name: 'Stock Audit List', route: '/reports/stock-audit-list', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_count_difference', name: 'Count Difference Report', route: '/reports/count-difference', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_stock_loss', name: 'Stock Loss Summary', route: '/reports/stock-loss', permission: { category: 'reports', key: 'inventoryReports' } },
      { id: 'reports_missing_items', name: 'Most Missing Items', route: '/reports/missing-items', permission: { category: 'reports', key: 'inventoryReports' } },
    ]
  },
  {
    name: 'Reports - Financial',
    modules: [
      { id: 'reports_ledger', name: 'Ledger Report', route: '/reports/ledger', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_trial_balance', name: 'Trial Balance', route: '/reports/trial-balance', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_profit_loss', name: 'Profit & Loss', route: '/reports/profit-loss', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_balance_sheet', name: 'Balance Sheet', route: '/reports/balance-sheet', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_cash_flow', name: 'Cash Flow Statement', route: '/reports/cash-flow', permission: { category: 'reports', key: 'financialReports' } },
      // 2026-10-27: had no rights check (shown to every user); now follows Financial Reports.
      { id: 'reports_cash_bank_balances', name: 'Cash & Bank Balances', route: '/reports/cash-bank-balances', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_tax_reports', name: 'Tax Reports', route: '/reports/tax-reports', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_financial_health', name: 'Financial Health', route: '/reports/financial-health', permission: { category: 'reports', key: 'financialReports' } },
      { id: 'reports_cash_flow_management', name: 'Cash Flow Management', route: '/reports/cash-flow-management', permission: { category: 'reports', key: 'financialReports' } },
    ]
  },
  {
    name: 'Reports - Salary',
    modules: [
      { id: 'reports_salary_register', name: 'Salary Register', route: '/reports/salary-register', permission: { category: 'reports', key: 'salaryReports' } },
      { id: 'reports_staff_loan_advance', name: 'Staff Loan & Advance', route: '/reports/staff-loan-advance', permission: { category: 'reports', key: 'salaryReports' } },
      { id: 'reports_statutory_contributions', name: 'Statutory Contributions', route: '/reports/statutory-contributions', permission: { category: 'reports', key: 'salaryReports' } },
    ]
  },
  {
    name: 'Reports - Jobs',
    modules: [
      { id: 'reports_job_cost_sheet', name: 'Job Cost Sheet', route: '/reports/job-cost-sheet', permission: { category: 'reports', key: 'jobReports' } },
      { id: 'reports_job_profit_loss', name: 'Job Profit & Loss', route: '/reports/job-profit-loss', permission: { category: 'reports', key: 'jobReports' } },
    ]
  },
  {
    name: 'Reports - Production',
    modules: [
      { id: 'reports_production_summary', name: 'Production Summary / Register', route: '/reports/production-summary', permission: { category: 'reports', key: 'productionReports' } },
      { id: 'reports_production_variance', name: 'Production Cost & Variance', route: '/reports/production-variance', permission: { category: 'reports', key: 'productionReports' } },
      { id: 'reports_material_issue', name: 'Material Issue Report', route: '/reports/material-issue', permission: { category: 'reports', key: 'productionReports' } },
      { id: 'reports_production_receipt', name: 'Production Receipt Report', route: '/reports/production-receipt', permission: { category: 'reports', key: 'productionReports' } },
    ]
  },
  {
    name: 'AI & Automation',
    modules: [
      { id: 'ai_assistant', name: 'AI Business Assistant', route: null },
      { id: 'ai_import', name: 'AI Studio', route: '/ai-import' },
    ]
  },
  {
    name: 'WhatsApp',
    modules: [
      // Phase 1: connection + manual Send to WhatsApp on Sale Invoice.
      // Phase 2: templates, logs, settings, auto-reminders, all doc types.
      // Phase 3: AI agent on incoming messages.
      // Module is default-OFF; super admin enables per company.
      { id: 'whatsapp_connect', name: 'Connect WhatsApp', route: '/whatsapp/connect', permission: { category: 'whatsapp', key: 'connection' } },
      { id: 'whatsapp_templates', name: 'Message Templates', route: '/whatsapp/templates', permission: { category: 'whatsapp', key: 'templates' } },
      { id: 'whatsapp_logs', name: 'Send Logs', route: '/whatsapp/logs', permission: { category: 'whatsapp', key: 'logs' } },
      { id: 'whatsapp_settings', name: 'WhatsApp Settings', route: '/whatsapp/settings', permission: { category: 'whatsapp', key: 'settings' } },
    ]
  },
  {
    name: 'Advanced',
    modules: [
      // When enabled: party dropdowns on Sales forms show vendors too, and
      // dropdowns on Purchase forms show customers too. Each item gets a
      // C/V badge. Useful for companies that buy from and sell to the same party.
      { id: 'unified_party_picker', name: 'Unified Party Picker (Customer ↔ Vendor)', route: null },
      // When enabled: each customer can carry a private item list with override
      // prices; sales forms auto-fill unitPrice from that list, and (if the
      // customer is flagged exclusive) the item picker is restricted to it.
      { id: 'sales_customer_pricing', name: 'Customer-wise Items & Pricing', route: null },
      // When enabled: each customer gets a "Sub-Category Discount" tab (discount %
      // per item sub-category). Items added for that customer on Sale Invoice,
      // Non-Tax Invoice, Quotation and Sale Order take that % as the line
      // discount. Not on POS. Default off.
      { id: 'customer_subcategory_discount', name: 'Customer Discount by Sub-Category', route: null },
      // When enabled: each vendor gets a "Custom Items & Pricing" tab; picking an
      // item for that vendor on PO, GRN, Purchase Invoice and Non-Tax Purchase
      // Invoice fills the rate from that list (returns keep the invoice price).
      // Default off.
      { id: 'purchases_vendor_pricing', name: 'Vendor-wise Items & Pricing', route: null },
      // Dashboard "What's new" widget — Claude-powered feature suggestions
      // that re-rank daily based on company usage. ON by default since the
      // value is universal, but a company can hide the widget anytime.
      { id: 'dashboard_whats_new', name: "What's New (AI Suggestions)", route: null },
      // Per-company addon: when ON, Items master gets an MRP field, Purchase
      // and Sale Invoices (tax + non-tax) show an MRP column and MRP total
      // row, and lineTotal includes the MRP component. Default OFF — other
      // companies see no UI change. Used by retail companies that track
      // per-item MRP on both buy and sell sides.
      { id: 'mrp_pricing', name: 'MRP on Invoices (Purchase + Sale)', route: null },
      // Per-company addon: when ON, every transaction form gets an Attachment
      // panel allowing ONE file (PDF/JPG/PNG, max 10MB) per document. File
      // lives in Firebase Storage and the URL is saved on the transaction doc.
      // Default OFF — SuperAdmin enables per company.
      { id: 'transaction_attachments', name: 'Document Attachments on Transactions', route: null },
      // Policy flag: when ON, Purchase Invoice (tax + non-tax) must be created
      // from a GRN parent. The "Link to PO" option is hidden and saving without
      // a GRN parent is blocked. Default OFF — all companies can do PO→PI
      // directly as before. SuperAdmin enables for clients whose policy
      // requires the full PO→GRN→PI chain.
      { id: 'purchases_enforce_grn_before_pi', name: 'Enforce GRN before Purchase Invoice', route: null },
      // Per-line expense account on the Purchase Invoice. When ON, a SERVICE
      // (non-stock) line gets an "Expense Account" picker and that line's value
      // debits the chosen expense account instead of the single footer debit
      // account. Restricted to service lines on purpose: service lines never
      // post stock, so the ledger and inventory can never diverge. Lines left
      // blank still fall back to the footer/Inventory Control account.
      // Default OFF — SuperAdmin enables per company.
      { id: 'purchases_line_expense_accounts', name: 'Expense Account per Purchase Line', route: null },
      // Hard-block negative inventory. When ON, posting Sale Invoice, Non-Tax
      // Sale Invoice, POS Counter Sale, or completing a Production Order is
      // BLOCKED if any line would drive item stock below zero at its location.
      // Default OFF — other companies keep the warn-and-allow behavior so
      // existing data and workflows are unaffected. SuperAdmin enables per
      // company for clients that need strict stock discipline.
      { id: 'inventory_block_negative', name: 'Block Negative Stock (SI/NSI/POS/PRO)', route: null },
      // Dimensional / area-based inventory. When ON, items can be flagged
      // "Has Size" — sale + purchase line forms then accept Length × Height ×
      // Thickness + Pieces and auto-compute SQft per piece, Total SQft, and
      // Amount. Stock is tracked in SQft (line.quantity = Total SQft) so
      // existing inventory reports work unchanged. Default OFF — irrelevant for
      // most retail/wholesale; turn on for marble, glass, mirror, tile, plywood
      // and similar slab/sheet businesses.
      { id: 'item_has_size', name: 'Has Size (Slab / Glass / Tile Items)', route: null },
      // Show Two Units (2026-10-10). Works with Units of Measure (master_units).
      // An item can pick two of its units to show together on sale/purchase
      // lines (e.g. Ctn + Mtr). Typing in either qty box fills the other using
      // the item's fixed conversion (decimals for part quantities). The line's
      // unit is the "Rate per" unit. Stock stays in the base unit. Default OFF.
      { id: 'item_two_units', name: 'Show Two Units (Qty in 2 units together)', route: null },
      // POS weight-scale plugin. When ON, POS shows a Web Serial scale widget
      // (Chrome/Edge) that reads an RS-232 weighing scale and lets the cashier
      // tap to capture the weight into a "Sold by weight" line's quantity.
      // Items gain a "Sold by weight" (kg, per-kg) checkbox. Default OFF — the
      // existing POS is untouched for everyone without this plugin.
      { id: 'pos_weight_scale', name: 'POS Weight Scale (RS-232)', route: null },
      // POS price-type selector. When ON, the POS bill header shows a
      // Retail / Wholesale / Dealer picker; newly added items seed their price
      // from the matching item-master field (falling back to Retail sale price).
      // Default OFF — POS is unchanged for everyone without this plugin.
      { id: 'pos_price_type', name: 'POS Price Type (Retail / Wholesale / Dealer)', route: null },
      // Customer Class Pricing. When ON, customers can be tagged Retail / Wholesale
      // / Dealer, and selecting a customer on a sales document auto-picks that price
      // tier for added items (still overridable per line). Default OFF.
      { id: 'customer_class', name: 'Customer Class Pricing (Retail / Wholesale / Dealer)', route: null },
      // 3rd Schedule sales-tax formula (FBR). When ON, each item gains a
      // "Sale Tax Type" (Normal / 3rd Schedule). On sales documents the GST base
      // then depends on the item: 3rd Schedule → GST on the FULL price (before
      // discount); Normal → GST on the price AFTER discount. When OFF the GST
      // base is unchanged from the company's current behaviour, so other
      // companies are completely unaffected. Default OFF.
      { id: 'sales_third_schedule_tax', name: '3rd Schedule Sales Tax (per-item GST before/after discount)', route: null },
      // POS bank card discounts. When ON: Maintain → Bank Discounts (offers per
      // bank / card type with %, cap, per-card limits, validity, shop/bank
      // share) and the POS Card payment gets an offer picker that applies the
      // discount before GST. Also unlocks the Bank Discount report. Default OFF
      // — POS and every other screen are unchanged without it.
      { id: 'pos_bank_discounts', name: 'POS Bank Card Discounts', route: '/master/bank-discounts', extraRoutes: ['/reports/bank-discounts'], permission: { category: 'masterData', key: 'banks' } },
      // Invoice Letterhead Choice. When ON: Print Settings gets an "Invoice
      // Letterheads" list (name, address, cell, email, NTN, sales tax no.), and
      // Print on a Sale Invoice / Non-Tax Sale Invoice asks which one to print
      // at the top — the company's own details, one of the extra letterheads, or
      // Blank. Print only: books, stock and ledgers stay under the one company.
      // Default OFF — printing is unchanged for everyone else.
      { id: 'print_invoice_letterheads', name: 'Invoice Letterhead Choice (print under another name / blank)', route: null },
    ]
  },
];

// Settings routes that are always-enabled but still need permission checks
export const ALWAYS_ENABLED_PERMISSIONS = {
  '/settings/company': { category: 'settings', key: 'myCompany' },
  '/settings/users': { category: 'settings', key: 'usersRoles' },
};

// Flat list of all toggleable (non-always-enabled) modules
const TOGGLEABLE_MODULES = MODULE_GROUPS.flatMap(g => g.modules);

// All modules combined
export const ALL_MODULES = [...ALWAYS_ENABLED, ...TOGGLEABLE_MODULES];

// Lookup: route path → module object
export const ROUTE_TO_MODULE = {};
ALL_MODULES.forEach(m => {
  if (m.route) ROUTE_TO_MODULE[m.route] = m;
  // A module may own extra pages (e.g. its report) gated by the same switch.
  (m.extraRoutes || []).forEach(r => { ROUTE_TO_MODULE[r] = m; });
});

// Lookup: module id → module object
export const MODULE_BY_ID = {};
ALL_MODULES.forEach(m => { MODULE_BY_ID[m.id] = m; });

// Lookup: route path → { category, key } permission required to view that route.
// Returns null if the route has no permission requirement (always accessible).
export const getRoutePermission = (routePath) => {
  if (ALWAYS_ENABLED_PERMISSIONS[routePath]) return ALWAYS_ENABLED_PERMISSIONS[routePath];
  const mod = ROUTE_TO_MODULE[routePath];
  return mod?.permission || null;
};

/**
 * Check if a module is enabled for a company.
 * - Always-enabled modules always return true.
 * - Default-disabled modules (e.g. branches) must be explicitly set to true.
 * - If enabledModules is null/undefined (field not set), all other modules are enabled (backwards compatible).
 * - If enabledModules[moduleId] === false, the module is disabled.
 * - Missing keys in enabledModules = enabled (except default-disabled modules).
 */
export const isModuleEnabledForCompany = (moduleId, enabledModules) => {
  if (ALWAYS_ENABLED_IDS.has(moduleId)) return true;
  // Loan Recovery follows the Loan / Advance screens: on wherever either of
  // them is on (unless Super Admin switched it off by itself).
  if (moduleId === 'salary_loan_recovery') {
    if (enabledModules && enabledModules[moduleId] === false) return false;
    return isModuleEnabledForCompany('salary_loan', enabledModules)
      || isModuleEnabledForCompany('salary_advance', enabledModules);
  }
  // Default-disabled modules must be explicitly enabled
  if (DEFAULT_DISABLED_IDS.has(moduleId)) {
    return enabledModules?.[moduleId] === true;
  }
  if (!enabledModules) return true;
  if (enabledModules[moduleId] === false) return false;
  return true;
};
